// AICA BottomNav Component
// Mobile bottom navigation bar for primary screens
// Hidden on tablet/desktop where Sidebar is shown
// Uses role-based navigation filtering (Phase 2 UX Redesign)

import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FileAudio,
  ShieldAlert,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useRole } from '@/contexts/RoleAdapter';

interface NavItem {
  id: string;
  translationKey: string;
  icon: LucideIcon;
  href: string;
}

// Major screens for mobile bottom navigation
const navigationItems: NavItem[] = [
  {
    id: 'dashboard',
    translationKey: 'nav.dashboard',
    icon: LayoutDashboard,
    href: '/dashboard',
  },
  {
    id: 'records',
    translationKey: 'nav.records',
    icon: FileAudio,
    href: '/records',
  },
  {
    id: 'alerts',
    translationKey: 'nav.alerts',
    icon: ShieldAlert,
    href: '/alerts',
  },
  {
    id: 'settings',
    translationKey: 'nav.settings',
    icon: Settings,
    href: '/settings',
  },
];

export interface BottomNavProps {
  className?: string;
}

export function BottomNav({ className }: BottomNavProps) {
  const matchRoute = useMatchRoute();
  const { t } = useTranslation();
  const { getVisibleScreens } = useRole();

  // Filter navigation items based on user role (Phase 2 UX Redesign)
  const visibleScreens = getVisibleScreens();
  const visibleItems = navigationItems.filter((item) => visibleScreens.includes(item.id));

  return (
    <nav
      className={cn(
        'md:hidden',
        'fixed bottom-0 left-0 right-0 z-sticky',
        'border-t border-border bg-bg-elevated',
        'safe-area-inset-bottom', // Respect iPhone notch/home indicator
        className
      )}
      aria-label={t('common:accessibility.navigation.mobile')}
    >
      <div className="flex justify-around items-center h-16 px-2">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const isActive = matchRoute({ to: item.href, fuzzy: true });

          return (
            <Link
              key={item.id}
              to={item.href}
              className={cn(
                'flex flex-col items-center justify-center gap-1',
                'min-w-[60px] px-3 py-2 rounded-button',
                'transition-all duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isActive
                  ? 'text-primary-fg bg-primary-dim'
                  : 'text-fg-muted hover:text-fg hover:bg-bg-subtle'
              )}
              aria-current={isActive ? 'page' : undefined}
              aria-label={t(item.translationKey)}
            >
              <Icon
                className={cn(
                  'h-5 w-5 transition-all duration-150',
                  isActive && 'text-primary'
                )}
                aria-hidden="true"
              />
              <span
                className={cn(
                  'text-micro font-medium transition-all duration-150',
                  isActive ? 'text-primary-fg' : 'text-fg-muted'
                )}
              >
                {t(item.translationKey)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
